import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';
import { SplashProvider } from './contexts/SplashContext';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingScreen from './components/ui/LoadingScreen';
import HomePage from './pages/HomePage';
import './i18n';

const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const SignupPage = React.lazy(() => import('./pages/SignupPage'));
const ResourcesPage = React.lazy(() => import('./pages/ResourcesPage'));
const ConsultationsPage = React.lazy(() => import('./pages/ConsultationsPage'));
const CommunityPage = React.lazy(() => import('./pages/CommunityPage'));
const ActivitiesPage = React.lazy(() => import('./pages/ActivitiesPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const AdminLayout = React.lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const UserManagement = React.lazy(() => import('./pages/admin/UserManagement'));
const ResourceManagement = React.lazy(() => import('./pages/admin/ResourceManagement'));
const ConsultationManagement = React.lazy(() => import('./pages/admin/ConsultationManagement'));

const guard = (el: React.ReactNode) => <ProtectedRoute>{el}</ProtectedRoute>;

const App: React.FC = () => (
  <HelmetProvider>
    <Suspense fallback={null}>
      <Router>
        <LanguageProvider>
          <ThemeProvider>
            <AuthProvider>
              <SplashProvider>
                <Routes>
                  <Route path="/" element={<Layout />}>
                    <Route index element={<HomePage />} />
                    <Route path="login" element={<LoginPage />} />
                    <Route path="signup" element={<SignupPage />} />
                    <Route path="learn/*" element={<ResourcesPage />} />
                    <Route path="resources/*" element={<Navigate to="/learn" replace />} />
                    <Route path="consultations/*" element={<ConsultationsPage />} />
                    <Route path="community/*" element={guard(<CommunityPage />)} />
                    <Route path="activities/*" element={<ActivitiesPage />} />
                    <Route path="profile/*" element={guard(<ProfilePage />)} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute admin>
                        <Suspense fallback={<LoadingScreen fullscreen />}>
                          <AdminLayout />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<AdminDashboard />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="resources" element={<ResourceManagement />} />
                    <Route path="consultations" element={<ConsultationManagement />} />
                    <Route path="*" element={<Navigate to="/admin" replace />} />
                  </Route>
                </Routes>
              </SplashProvider>
            </AuthProvider>
          </ThemeProvider>
        </LanguageProvider>
      </Router>
    </Suspense>
  </HelmetProvider>
);

export default App;
